import { API_URL } from "api";
import { T_Api_Error_Res } from "api/api.types";
import { AxiosError } from "axios";
import dayjs, { Dayjs } from "dayjs";
import { FREQUENCY_TYPES } from "dtos/loan.dto";
import { DailyCashFlowDataPoint } from "dtos/forecast.dto";
import Fuse from "fuse.js";
import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import { twColors } from "./constants";
import { BusinessInfo, ClientDetails, SelectedBank } from "dtos/application.dto";
import { MakeOptional } from "types/_utils.types";
import { RoleTypes } from "dtos/user.dto";
import { ActiveFilter, Channel, Thread } from "types/communication.types";

export const mergeArraysUnique = <T extends any[]>(...items: T) => {
  const uniqueItems = new Set<unknown>();
  for (const item of items) {
    uniqueItems.add(item);
  }

  return Array.from(uniqueItems).flat() as T;
};

export const toRoleDisplayNames = (role: RoleTypes) => {
  if (role === RoleTypes.ISO) return "ISO";
  if (role === RoleTypes.SUPERADMIN) return "Super Admin";
  return role;
};

export function getFirstAlphabet(input: string): string | null {
  for (const char of input) {
    if (/[a-zA-Z]/.test(char)) {
      return char;
    }
  }

  return null;
}

export const toCapitalCase = (val: string | undefined | null) => {
  return (val ?? "")
    .split(/[_.,\s]+/)
    .filter((seg) => seg?.length)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

export const getInitials = (name?: string | string[], length = 2) => {
  if (!name) return null;
  const segments = (Array.isArray(name) ? name : name.trim().split(" ")).filter((seg) => seg.length > 0);

  if (segments.length >= length)
    return segments
      .slice(0, length)
      .map((seg) => seg.at(0))
      .join("")
      .toLocaleUpperCase();
  if (segments[0].length >= length) return segments[0].toLocaleUpperCase().slice(0, length);
  return segments.map(toCapitalCase).join(" ").slice(length).trim();
};

export const pickRandom = <T>(arr: T[]) => {
  const index = Math.floor(arr.length * Math.random());
  return arr.at(index) ?? arr[0];
};

export function isArrayEqual(a: unknown[], b: unknown[]) {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (Array.isArray(a[i])) return isArrayEqual(a[i] as unknown[], b[i] as unknown[]);
    else if (typeof a[i] === "object") return deepEqual(a[i] as object, b[i] as object);
    else if (a !== b) return false;
  }

  return true;
}

export function deepEqual(a: object, b: object) {
  // return checkEquality(a, b);
  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);

  if (aKeys.length !== bKeys.length) return false;
  return aKeys.every((key: string): boolean => {
    const aVal = a[key as keyof typeof a] as unknown;
    const bVal = b[key as keyof typeof b] as unknown;

    if (typeof aVal !== typeof bVal) return false;
    else if (Array.isArray(aVal)) return isArrayEqual(aVal, bVal as unknown[]);
    else if (!aVal || !bVal) return aVal === bVal;
    else if (typeof aVal === "object") return deepEqual(aVal, bVal);
    return aVal === bVal;
  });
}

export function toTitleCase(strs: string | undefined, config?: { sep?: string; forceLower?: boolean }) {
  const sep = config?.sep ?? " ",
    forceLower = config?.forceLower ?? false;
  if (!strs) return;
  return strs
    .split(sep)
    .map((el) => {
      let rest = el.slice(1);
      if (forceLower) rest = rest.toLowerCase();
      return el[0]?.toUpperCase() + rest;
    })
    .join(" ");
}

export const formatMoney = (price: number | undefined, showDecimal?: boolean, showCurrency: boolean = true) => {
  if (typeof price !== "number") return "-";

  let option: {
    maximumFractionDigits?: number;
    trailingZeroDisplay?: string;
  } = { trailingZeroDisplay: "stripIfInteger" };

  if (typeof showDecimal === "boolean") {
    option = { maximumFractionDigits: showDecimal ? 2 : 0 };
  }

  const formatter = new Intl.NumberFormat(
    "en-US",
    showCurrency
      ? {
          style: "currency",
          currency: "USD",
          ...option,
        }
      : {
          style: "decimal",
          ...option,
        },
  );

  return formatter.format(price);
};

export const parseMoney = (value?: string): number | undefined => {
  if (!value) return undefined;

  const parsed = Number(value.replace(/[^0-9.-]+/g, ""));
  return isNaN(parsed) ? undefined : parsed;
};

export function formatDate(date: Date | string) {
  return dayjs(date).format(`DD MMM YYYY`);
}

export function formatDateTime(date: Date | string) {
  return dayjs(date).format(`hh:mma • DD MMM YYYY`);
}
export function formatTime(date: Date | string) {
  if (dayjs().isSame(date, "day")) return dayjs(date).format(`hh:mm a`);

  return dayjs(date).format("DD MMM");
}

export function getDriveUrl(fileSlug: string | undefined) {
  return `${API_URL}/file/${fileSlug}`;
}

export function slugify(str: string, options: { lower: boolean } = { lower: true }) {
  str = str.replace(/^\s+|\s+$/g, ""); // trim leading/trailing white space
  if (options.lower) str = str.toLowerCase(); // convert string to lowercase
  str = str
    .replace(/[^a-z0-9 -]/g, "") // remove any non-alphanumeric characters
    .replace(/\s+/g, "-") // replace spaces with hyphens
    .replace(/-+/g, "-"); // remove consecutive hyphens
  return str;
}

/**
 * Browser-compatible randomBytes() similar to Node.js crypto.randomBytes
 */
export function randomBytes(length: number): Uint8Array {
  if (!Number.isInteger(length) || length < 0) {
    throw new TypeError("length must be a non-negative integer");
  }

  const bytes = new Uint8Array(length);

  // Browser Web Crypto API
  crypto.getRandomValues(bytes);

  return bytes;
}

/**
 * Generally for checking boolean query params
 */
export function isTruthful(value: unknown) {
  if (typeof value === "string") return value === "true" || value === "1";
  else if (typeof value === "boolean") return value === true;
  else if (typeof value === "number") return value === 1;
  return false;
}

export function validateNumber(value: string | undefined) {
  if (typeof value === "string") {
    value = value?.replace(/\D/g, "");
  } else if (typeof value === "number") {
    value = String(value);
  }

  return value?.length === 11 || value?.length === 0;
}

export function phoneFormatter(value: string | undefined) {
  if (!value) return "";
  const onlyNumbers = `${value}`.replace(/\D/g, "");

  const formatted = onlyNumbers.replace(/^(\d{0,1})(\d{0,3})(\d{0,3})(\d{0,4})$/, (match, p1, p2, p3, p4) => {
    return `+${p1 ?? ""} ${p2 ? `${p2}` : ""}${p3 ? `-${p3}` : ""}${p4 ? `-${p4}` : ""}`;
  });
  return formatted.trim();
}

export function formatPhoneDisplay(value: string | undefined): string {
  if (!value) return "";

  const digits = value.replace(/\D/g, "");

  if (!value.startsWith("+")) return value;

  // India: +91 XXXXX XXXXX
  if (digits.startsWith("91") && digits.length === 12) {
    const subscriber = digits.slice(2);
    return `+91 ${subscriber.slice(0, 5)} ${subscriber.slice(5)}`;
  }

  // North America: +1 (XXX) XXX-XXXX
  if (digits.startsWith("1") && digits.length === 11) {
    const subscriber = digits.slice(1);
    return `+1 (${subscriber.slice(0, 3)}) ${subscriber.slice(3, 6)}-${subscriber.slice(6)}`;
  }

  return value;
}

export function numberParser(value: string | undefined) {
  const parsed = value?.replace(/\D/g, "");
  return parsed ?? "";
}

export function getErrorMessage<T extends AxiosError<T_Api_Error_Res>>(error: T | unknown) {
  if (error instanceof AxiosError) {
    const err = error as T;
    return (
      err?.response?.data?.result?.message ??
      (typeof err?.response?.data?.result?.error === "string" ? err?.response?.data?.result?.error : undefined) ??
      err?.response?.data?.message ??
      err?.message
    );
  } else if (error instanceof Error) {
    return error?.message;
  } else return String(error);
}

export function calculateAPR(
  totalFundedAmount: number,
  instalmentAmount: number,
  instalmentFrequency: FREQUENCY_TYPES | undefined = FREQUENCY_TYPES.DAILY,
  tenure: number,
): number {
  const r = rate(tenure, -instalmentAmount, totalFundedAmount);
  const multipier: Record<typeof instalmentFrequency, number> = {
    [FREQUENCY_TYPES.DAILY]: 365,
    [FREQUENCY_TYPES.WEEKLY]: 52,
    [FREQUENCY_TYPES.MONTHLY]: 12,
    [FREQUENCY_TYPES.YEARLY]: 1,
  };
  return r * multipier[instalmentFrequency] * 100;
}

function rate(periods: number, payment: number, present: number, future?: number, type?: number, guess?: number) {
  guess = guess === undefined ? 0.01 : guess;
  future = future === undefined ? 0 : future;
  type = type === undefined ? 0 : type;

  // Set maximum epsilon for end of iteration
  const epsMax = 1e-8;

  // Set maximum number of iterations
  const iterMax = 40;

  // Implement Newton's method
  let y: number,
    y0: number,
    y1: number,
    x0: number,
    x1 = 0,
    f = 0,
    i = 0;
  let rate = guess;
  if (Math.abs(rate) < epsMax) {
    y = present * (1 + periods * rate) + payment * (1 + rate * type) * periods + future;
  } else {
    f = Math.exp(periods * Math.log(1 + rate));
    y = present * f + payment * (1 / rate + type) * (f - 1) + future;
  }
  y0 = present + payment * periods + future;
  y1 = present * f + payment * (1 / rate + type) * (f - 1) + future;
  i = x0 = 0;
  x1 = rate;
  while (Math.abs(y0 - y1) > epsMax && i < iterMax) {
    rate = (y1 * x0 - y0 * x1) / (y1 - y0);
    x0 = x1;
    x1 = rate;
    if (Math.abs(rate) < epsMax) {
      y = present * (1 + periods * rate) + payment * (1 + rate * type) * periods + future;
    } else {
      f = Math.exp(periods * Math.log(1 + rate));
      y = present * f + payment * (1 / rate + type) * (f - 1) + future;
    }
    y0 = y1;
    y1 = y;
    ++i;
  }
  return rate;
}

export function calculateIRR(
  advanceAmount: number,
  totalPayback: number,
  instalmentAmount: number,
  paymentFrequency: "daily" | "weekly" | "monthly",
  maxIterations: number = 100,
  precision: number = 1e-6,
): { irr: number | undefined; arr: number | undefined } {
  const numberOfInstalments = Math.ceil(totalPayback / instalmentAmount);

  const frequencyMap: Record<string, number> = {
    daily: 365,
    weekly: 52,
    monthly: 12,
  };

  const periodsInYear = frequencyMap[paymentFrequency];
  if (!periodsInYear) {
    throw new Error("Invalid payment frequency. Use 'daily', 'weekly', or 'monthly'.");
  }

  let irr = 0.05; // Assuming Low 10%

  for (let iteration = 0; iteration < maxIterations; iteration++) {
    let npv = -advanceAmount;
    let derivative = 0;

    // Calculate NPV and its derivative
    for (let t = 1; t <= numberOfInstalments; t++) {
      const discountFactor = Math.pow(1 + irr, t / periodsInYear);
      npv += instalmentAmount / discountFactor;
      derivative -= (t / periodsInYear) * (instalmentAmount / Math.pow(1 + irr, t / periodsInYear + 1));
    }

    // Newton-Raphson adjustment
    const irrAdjustment = npv / derivative;
    irr -= irrAdjustment;

    // Break if the IRR adjustment is within the specified precision
    if (Math.abs(irrAdjustment) < precision) {
      return {
        irr: irr,
        arr: irr * periodsInYear,
      }; // Return annualized IRR
    }
  }
  return { irr: undefined, arr: undefined };
  // throw new Error("IRR did not converge within the maximum number of iterations.");
}

export function moneyFormatter(value: number | undefined) {
  return `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

export function moneyParser(value: string | undefined) {
  return value?.replace(/(,*)/g, "") as unknown as number;
}

export function formatCurrYear(date: Dayjs) {
  const isCurrYear = date.isSame(dayjs(), "year");
  return date.format(`DD MMM${isCurrYear ? "" : " YYYY"}`);
}
/**
 * **CAUTION**: It is expected both the date are ISO Date strings
 * @param startDate string
 * @param endDate string
 */
export function collapsedDateRangeText(start: string | undefined, end: string | undefined) {
  const startDate = dayjs(start);
  const endDate = dayjs(end);
  if (!start) {
    return `Till ${formatCurrYear(endDate)}`;
  }
  if (!end) {
    return `${formatCurrYear(startDate)} - Today`;
  }
  const isSameDate = startDate.isSame(endDate, "date");
  const isSameMonth = startDate.isSame(endDate, "month");
  const isSameYear = startDate.isSame(endDate, "year");

  if (isSameDate) {
    return formatCurrYear(endDate);
  } else if (isSameMonth) {
    return `${startDate.format("DD")} - ${formatCurrYear(endDate)}`;
  } else if (isSameYear) {
    return `${startDate.format("DD MMM")} - ${formatCurrYear(endDate)}`;
  } else {
    return `${formatDate(start)} - ${formatDate(end)}`;
  }
}

export function formatDuration(minutes: number) {
  if (minutes > 60) {
    const hour = minutes / 60;
    const restMinutes = minutes % 60;
    return `${hour.toFixed(0)}h ${Math.floor(restMinutes)}m`;
  } else if (minutes === 0) {
    return "Not recorded yet";
  }
  return `${Math.floor(minutes)}m ${Math.round((minutes - Math.floor(minutes)) * 60)}s`;
}

export function delay(milliseconds: number, cb?: () => void): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(() => {
      cb?.();
      resolve();
    }, milliseconds);
  });
}

export const transformDataForRecharts = (apiData: DailyCashFlowDataPoint[]) => {
  return apiData.map((entry) => ({
    name: dayjs(entry.x).format("MMM YYYY"), // Show only the month
    uv: new Date(entry.x).getTime(), // Keep actual x-axis time for plotting
    pv: entry.y, // y-axis value
  }));
};

export const parseNumericToInternationalFormat = (value: number | undefined) => {
  if (!value) return;

  return `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

export const getDebtBasedWeeks = (total_debt: number, week_multiplier: number = 4.33) => {
  let total_weeks = 0;
  let interest_weeks_3 = 15;
  if (total_debt < 25001) {
    total_weeks = Math.ceil(week_multiplier * 6);
  } else if (total_debt < 50001) {
    total_weeks = Math.ceil(week_multiplier * 8);
    interest_weeks_3 = 20;
  } else if (total_debt < 125001) {
    total_weeks = Math.ceil(week_multiplier * 12);
    interest_weeks_3 = 30;
  } else if (total_debt < 250001) {
    total_weeks = Math.ceil(week_multiplier * 18);
    interest_weeks_3 = 40;
  } else if (total_debt < 500001) {
    total_weeks = Math.ceil(week_multiplier * 24);
    interest_weeks_3 = 50;
  } else {
    total_weeks = Math.ceil(week_multiplier * 30);
    interest_weeks_3 = 60;
  }
  return { total_weeks, interest_weeks_3 };
};

export function formatCurrencyShort(value: number, currencySymbol: string = "$", decimals: number = 0): string {
  if (value === 0) return `${currencySymbol}0`;

  const absValue = Math.abs(value);
  const sign = value < 0 ? "-" : "";

  const suffixes = [
    { value: 1e12, suffix: "T" },
    { value: 1e9, suffix: "B" },
    { value: 1e6, suffix: "M" },
    { value: 1e3, suffix: "K" },
  ];

  for (const { value: divisor, suffix } of suffixes) {
    if (absValue >= divisor) {
      return `${sign}${currencySymbol}${(absValue / divisor).toFixed(decimals)}${suffix}`;
    }
  }

  return `${sign}${currencySymbol}${absValue.toFixed(decimals)}`;
}

export const getMonthByCode = (month: number) => {
  const mapping = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  return mapping[month % 12]?.substring(0, 3);
};

export const getDayByCode = (day: number) => {
  const mapping = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

  return mapping[day % 7];
};

export function formatForRecharts(data: ({ month: number; year: number } & Record<string, number>)[]) {
  return data.map((el) => ({
    ...el,
    date: `${String(el?.month).padStart(2, "0")}/${String(el.year).substring(2, 4)}`,
  }));
}

export function formatPercent(num: number) {
  return num.toLocaleString("en-US", { style: "percent" });
}

export function getLifeCycleStageColor(stage: string): string {
  switch (stage) {
    case "":
      return "#E5E7EBF";
    case "banks":
      return "#3B82F6";
    case "lawsuits":
      return "#EF4444";
    case "loans":
      return "#8B5CF6";
    case "strategy":
      return "#6366F1";
    case "personal_info":
      return "#10B981";
    case "signatures":
      return "#F59E0B";
    case "onboarding":
      return "#14B8A6";
    default:
      return "#9CA3AF";
  }
}

export function removeQuotations(input: string) {
  return input.replace(RegExp('"', "g"), "");
}

export function parseEmailTitle(input: string): string {
  const match = input.match(/^(.*?)\s*<(.+?)>$/);

  if (match) {
    const title = match[1].trim();
    if (title) return removeQuotations(title);
  }

  return removeQuotations(parseEmailAddress(input).split("@")[0].trim());
}

export function parseEmailAddress(input: string) {
  const match = input.match(/<(.+?)>/) || input.match(/([^\s<>]+@[^\s<>]+)/);
  return match ? match[1].trim() : input.trim();
}

export function timeAgo(date: Date | string | number): string {
  const d = dayjs(date);
  const now = dayjs();

  const seconds = now.diff(d, "second");
  if (seconds < 60) return `${seconds}s`;

  const minutes = now.diff(d, "minute");
  if (minutes < 60) return `${minutes}m`;

  const hours = now.diff(d, "hour");
  if (hours < 24) return `${hours}h`;

  const days = now.diff(d, "day");
  if (days < 7) return `${days}d`;

  const weeks = now.diff(d, "week");
  if (weeks < 4) return `${weeks}w`;

  const months = now.diff(d, "month");
  if (months < 12) return `${months}mo`;

  const years = now.diff(d, "year");
  return `${years}y`;
}

export function extractContentType(mimeType?: string): ContentType {
  if (!mimeType) return "unknown";

  const [type, subtype] = mimeType.split("/");

  if (type === "image") return "image";
  if (type === "video") return "video";
  if (type === "audio") return "audio";
  if (type === "text") return "text";

  if (type === "application") {
    if (subtype === "pdf") return "pdf";

    if (subtype.includes("zip") || subtype.includes("rar") || subtype.includes("7z") || subtype.includes("tar")) {
      return "archive";
    }

    if (subtype.includes("excel") || subtype.includes("spreadsheet")) {
      return "spreadsheet";
    }

    if (subtype.includes("word") || subtype.includes("msword") || subtype.includes("officedocument.wordprocessingml")) {
      return "document";
    }

    if (subtype.includes("powerpoint") || subtype.includes("presentationml")) {
      return "presentation";
    }
  }

  return "binary";
}

export const fuzzyMatch = (arr: string[], searchStr: string) => {
  const options = {
    includeScore: true,
    threshold: 0.3,
    ignoreLocation: true,
  };

  const fuse = new Fuse(arr, options);

  const result = fuse.search(searchStr);

  return result.length ? result.map((r) => r.item) : [];
};

export const transformAndCombineDataForRecharts = (
  currentData: DailyCashFlowDataPoint[],
  forecastedData: DailyCashFlowDataPoint[],
) => {
  const dataMap = new Map();

  sortByDate(currentData).forEach((entry) => {
    const key = entry.x;
    dataMap.set(key, {
      name: dayjs(entry.x).format("MMM YYYY"),
      uv: entry.x,
      originalData: entry.y,
      forecastedData: null,
    });
  });

  sortByDate(forecastedData).forEach((entry) => {
    const key = entry.x;
    if (dataMap.has(key)) {
      dataMap.get(key).forecastedData = entry.y;
    } else {
      dataMap.set(key, {
        name: dayjs(entry.x).format("MMM YYYY"),
        uv: entry.x,
        originalData: null,
        forecastedData: entry.y,
      });
    }
  });

  return Array.from(dataMap.values()).sort((a, b) => a.uv - b.uv);
};

export const sortByDate = (data: DailyCashFlowDataPoint[]): DailyCashFlowDataPoint[] => {
  return data.sort((a, b) => dayjs(a.x).valueOf() - dayjs(b.x).valueOf());
};

type RawDailyCF = {
  date: string;
  cashflow: number;
};

type NormalizedPoint = {
  date: string; // ISO string
  value: number; // 👈 ALWAYS plotted on Y-axis
  lower?: number;
  upper?: number;
  confidence?: number;
  type: "actual" | "backtest" | "forecast";
};

type RawBacktest = {
  date: string;
  actual: number;
  forecast: number;
  lower_bound: number;
  upper_bound: number;
  confidence_score: number;
};

type RawForecast = {
  x: string;
  y: number;
  lower_bound: number;
  upper_bound: number;
  confidence_score: number;
};

const toISO = (date: string) => new Date(`${date}T00:00:00.000Z`).toISOString();

export function normalizeCashflowData({
  forecastData,
  backTestData,
  historicalData,
}: {
  historicalData: DailyCashFlowDataPoint[];
  backTestData: RawBacktest[];
  forecastData: RawForecast[];
}) {
  const historical: NormalizedPoint[] = historicalData.map((d) => ({
    date: d.x,
    value: Number(d.y.toFixed(2)),
    type: "actual",
  }));

  const backtest: NormalizedPoint[] = backTestData.map((r: RawBacktest) => ({
    date: toISO(r.date),
    value: Number(r.forecast?.toFixed(2)), // 👈 choose forecast as plotted value
    lower: Number(r.lower_bound?.toFixed(2)),
    upper: Number(r.upper_bound?.toFixed(2)),
    confidence: Number(r.confidence_score?.toFixed(2)),
    type: "backtest",
  }));

  const forecast: NormalizedPoint[] = forecastData.map((f: RawForecast) => ({
    date: f.x,
    value: Number(f.y.toFixed(2)),
    lower: Number(f.lower_bound?.toFixed(2)),
    upper: Number(f.upper_bound?.toFixed(2)),
    confidence: Number(f.confidence_score?.toFixed(2)),
    type: "forecast",
  }));

  return {
    historical,
    backtest,
    forecast,

    // 👇 optional: single merged timeline
    merged: [...historical, ...backtest, ...forecast].sort((a, b) => +new Date(a.date) - +new Date(b.date)),
  };
}
export type ContentType =
  | "image"
  | "video"
  | "audio"
  | "pdf"
  | "text"
  | "archive"
  | "spreadsheet"
  | "document"
  | "presentation"
  | "binary"
  | "unknown";

export const getContentTypeFromFile = (file: File): ContentType => {
  const mime = file.type;
  const ext = file.name.split(".").pop()?.toLowerCase();

  if (mime.startsWith("image/")) return "image";
  if (mime.startsWith("video/")) return "video";
  if (mime.startsWith("audio/")) return "audio";

  if (mime === "application/pdf" || ext === "pdf") return "pdf";

  if (mime.startsWith("text/") || ["txt", "md", "csv"].includes(ext ?? "")) return "text";

  if (["zip", "rar", "7z", "tar", "gz"].includes(ext ?? "")) return "archive";

  if (
    mime === "application/vnd.ms-excel" ||
    mime === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
    ["xls", "xlsx"].includes(ext ?? "")
  )
    return "spreadsheet";

  if (
    mime === "application/msword" ||
    mime === "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
    ["doc", "docx"].includes(ext ?? "")
  )
    return "document";

  if (
    mime === "application/vnd.ms-powerpoint" ||
    mime === "application/vnd.openxmlformats-officedocument.presentationml.presentation" ||
    ["ppt", "pptx"].includes(ext ?? "")
  )
    return "presentation";

  if (!mime && ext) return "binary";

  return "unknown";
};

export function formatBytes(bytes: number, decimals: number = 2): string {
  if (!Number.isFinite(bytes) || bytes < 0) return "0 B";
  if (bytes === 0) return "0 B";

  const KB = 1024;
  const MB = KB * 1024;

  if (bytes < KB) {
    return `${bytes} B`;
  }

  if (bytes < MB) {
    return `${(bytes / KB).toFixed(decimals)} KB`;
  }

  return `${(bytes / MB).toFixed(decimals)} MB`;
}

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function humanFileSize(bytes: number, dp = 1) {
  const thresh = 1024;

  if (Math.abs(bytes) < thresh) {
    return bytes + " B";
  }

  const units = ["kB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];
  let u = -1;
  const r = 10 ** dp;

  do {
    bytes /= thresh;
    ++u;
  } while (Math.round(Math.abs(bytes) * r) / r >= thresh && u < units.length - 1);

  return bytes.toFixed(dp) + " " + units[u];
}
export function formatNumber(num: number) {
  return num.toLocaleString("en-US", { style: "decimal", maximumFractionDigits: 2, minimumFractionDigits: 2 });
}

export enum PDFTypes {
  STRATEGY_ONE = "strategy_one",
  STRATEGY_TWO = "strategy_two",
  // STRATEGY_THREE = "strategy_three",
  // CUSTOM = "custom",
  CITADEL = "citadel",
  LYL = "lyl",
}

export function getPdfTypeFromPlan(plan: string): string | null {
  if (plan === "Fixed Plan 1") return PDFTypes.STRATEGY_ONE;
  if (plan === "Fixed Plan 2") return PDFTypes.STRATEGY_TWO;
  if (plan === "Fixed Plan 3" || plan.startsWith("Custom")) return PDFTypes.LYL;

  return null;
}

type ShortNumberResult = {
  short: string;
  actual: number;
};

export function formatNumberShort(value: number): ShortNumberResult {
  if (value === null || value === undefined || isNaN(value)) {
    return { short: "0", actual: 0 };
  }

  const absValue = Math.abs(value);

  const units = [
    { threshold: 1_000_000_000, suffix: "B" },
    { threshold: 1_000_000, suffix: "M" },
    { threshold: 1_000, suffix: "K" },
  ];

  for (const unit of units) {
    if (absValue >= unit.threshold) {
      const formatted = (value / unit.threshold)
        .toFixed(1) // only 1 decimal
        .replace(/\.0$/, ""); // remove .0 if unnecessary

      return {
        short: `${formatted}${unit.suffix}`,
        actual: value,
      };
    }
  }

  return {
    short: value.toString(),
    actual: value,
  };
}

/**
 * Template Renderer Utility
 * Renders templates by replacing variables with mock data
 */

/**
 * Renders template by replacing variables with mock data
 * @param content - Template content with {{variable}} patterns
 * @param mockData - Key-value pairs of variable names and their values
 * @returns Rendered content with variables replaced
 */
export function renderTemplate(content: string, mockData: Record<string, string>): string {
  if (!content) return "";

  return content.replace(/\{\{([^}]+)\}\}/g, (match, variable) => {
    const trimmedVar = variable.trim();
    return mockData[trimmedVar] ?? match; // Keep original if no mock data
  });
}

/**
 * Identifies missing variables that don't have mock data
 * @param content - Template content with {{variable}} patterns
 * @param mockData - Key-value pairs of variable names and their values
 * @returns Array of variable names without mock data
 */
export function getMissingMockData(content: string, mockData: Record<string, string>): string[] {
  if (!content) return [];

  const regex = /\{\{([^}]+)\}\}/g;
  const matches = [...content.matchAll(regex)];
  const variables = matches.map((match) => match[1].trim());
  const uniqueVariables = [...new Set(variables)];

  return uniqueVariables.filter((variable) => !mockData[variable]);
}

/**
 * Renders template with highlighting for missing variables
 * @param content - Template content with {{variable}} patterns
 * @param mockData - Key-value pairs of variable names and their values
 * @returns Rendered content with missing variables highlighted
 */
export function renderTemplateWithHighlights(content: string, mockData: Record<string, string>): string {
  if (!content) return "";

  return content.replace(/\{\{([^}]+)\}\}/g, (match, variable) => {
    const trimmedVar = variable.trim();
    if (mockData[trimmedVar]) {
      return mockData[trimmedVar];
    }
    // Highlight missing variables
    return `<span style="background-color: #fef3c7; color: #92400e; padding: 0 4px; border-radius: 2px;">${match}</span>`;
  });
}

/**
 * Variable Extractor Utility
 * Extracts unique variables from template content using {{variable}} syntax
 */

/**
 * Extracts all unique variables from content
 * @param content - String that may contain {{variable}} patterns
 * @returns Array of unique variable names (without braces), sorted alphabetically
 */
export function extractVariables(content: string): string[] {
  if (!content) return [];

  const regex = /\{\{([^}]+)\}\}/g;
  const matches = [...content.matchAll(regex)];
  const variables = matches.map((match) => match[1].trim());

  // Remove duplicates and sort alphabetically
  return [...new Set(variables)].sort();
}

/**
 * Extracts variables from both subject and body, combining them
 * @param subject - Email subject line
 * @param body - Email body content (HTML)
 * @returns Array of unique variable names from both fields
 */
export function extractAllVariables(subject: string, body: string): string[] {
  const combinedContent = `${subject || ""} ${body || ""}`;
  return extractVariables(combinedContent);
}

/**
 * Checks if a string contains any variables
 * @param content - String to check
 * @returns Boolean indicating if variables exist
 */
export function hasVariables(content: string): boolean {
  const regex = /\{\{([^}]+)\}\}/;
  return regex.test(content);
}

/**
 * Wraps a variable name in the {{}} syntax
 * @param variableName - Name of the variable
 * @returns Formatted variable string
 */
export function formatVariable(variableName: string): string {
  return `{{${variableName}}}`;
}

export const safeDivide = (numerator: number, denominator: number) => (denominator > 0 ? numerator / denominator : 0);

type SafeOperateAccepts = number | string | undefined | null;
class SafeOperate {
  private toNumber(value: SafeOperateAccepts) {
    if (value === null || value === undefined) return undefined;
    if (typeof value === "number") return value;
    if (!Number.isNaN(Number.parseInt(value))) return Number.parseInt(value);
    return undefined;
  }

  constructor(private _value: SafeOperateAccepts) {}

  add(value: SafeOperateAccepts) {
    if (this.toNumber(value) !== undefined && this.value !== undefined) {
      this._value = this.toNumber(this._value)! + this.toNumber(value)!;
    }

    return this;
  }

  subtract(value: SafeOperateAccepts) {
    if (this.toNumber(value) !== undefined && this.value !== undefined) {
      this._value = this.toNumber(this._value)! - this.toNumber(value)!;
    }

    return this;
  }

  multiply(value: SafeOperateAccepts) {
    if (this.toNumber(value) !== undefined && this.value !== undefined) {
      this._value = this.toNumber(this._value)! * this.toNumber(value)!;
    }

    return this;
  }

  public get value() {
    this._value = this.toNumber(this._value);
    return this._value;
  }
}

export const Safe = (value: SafeOperateAccepts) => {
  return new SafeOperate(value);
};

export const round = (num: number, decimalPlaces = 2) => {
  const factor = Math.pow(10, decimalPlaces);
  return Math.round(num * factor) / factor;

  // return Number(num.toFixed(decimalPlaces));
};

export const ceilToClosest99 = (num: number) => {
  const remainder = num % 100;
  return num + (99 - remainder);
};

export const floorToClosest99 = (num: number) => {
  return Math.floor(num % 100) === 99 ? num : ceilToClosest99(num > 99 ? num - 100 : num);
};

export const parseNumber = (val: unknown) => {
  if (val === undefined || val === null || Number.isNaN(val)) return null;
  return Number(val);
};

export const asPercentage = (val: unknown) => {
  const num = parseNumber(val);
  if (num === null || num < 0 || num > 100) return null;
  if (num >= 1) return num / 100;
  return num;
};

export const getDecimals = (val: number | undefined | null) => {
  if (!val) return 0;
  return val - Math.floor(val);
};

export const getValueColor = (value: number) => {
  if (value > 0) return twColors.green600;
  if (value < 0) return twColors.red600;
  return undefined;
};

export class ObjectId {
  private readonly raw: string | undefined;

  constructor(id: undefined | null);
  constructor(id: string);
  constructor(id: object);
  constructor(id: ObjectId);
  constructor(id: undefined | null | string | ObjectId | object) {
    if (id === undefined || id === null) {
      return;
    } else if (typeof id === "string") {
      this.raw = id;
    } else if (id instanceof ObjectId) {
      this.raw = id?.raw;
    } else if (typeof id === "object") {
      if ("_id" in id && typeof id._id === "string") this.raw = id._id;
      else if ("id" in id && typeof id.id === "string") this.raw = id.id;
    }
  }

  toString() {
    return this.raw?.toString();
  }

  toJSON() {
    return this.raw?.toString();
  }

  equals(param: undefined | string | ObjectId | Partial<Record<"id" | "_id", ObjectId | string>>) {
    if (!param) return false;

    let value: string | undefined;
    if (param instanceof ObjectId || typeof param === "string") {
      value = param.toString();
    } else if (param["_id"] !== undefined) {
      value = param["_id"].toString();
    } else if (param["id"] !== undefined) {
      value = param["id"].toString();
    }

    return value === this.toString();
  }
}

export function validateRequiredFields({
  businessInfo,
  clientDetails,
  selectedBank,
}: {
  clientDetails: ClientDetails;
  businessInfo: BusinessInfo;
  selectedBank: SelectedBank;
}): boolean {
  // Guard: if any top-level object is missing entirely
  if (!clientDetails || !businessInfo || !selectedBank) return false;

  // ClientDetails required fields
  if (!clientDetails.name || !clientDetails.ssn) return false;

  // BusinessInfo required fields
  if (
    !businessInfo.company_name ||
    !businessInfo.business_type ||
    !businessInfo.city || // ← you had a bug here, missing the `!`
    !businessInfo.ein_number ||
    !businessInfo.email ||
    !businessInfo.phone ||
    !businessInfo.zip_code ||
    !businessInfo.street_address ||
    !businessInfo.state
  )
    return false;

  // SelectedBank required fields
  if (!selectedBank.bank_name || !selectedBank.account_no || !selectedBank.name_on_account || !selectedBank.routing_no)
    return false;

  return true;
}

// DOES NOT WORK
export const omit = <O extends Record<string, unknown>, K extends keyof O>(base: O, ...keys: K[]): Omit<O, K> => {
  const newObj = {};
  for (const key in base) {
    if (keys.includes(key as unknown as K)) continue;
    Object.assign(newObj, { key: base[key] });
  }

  return newObj as Omit<O, K>;
};

// DOES NOT WORK
export const omitAndMakeOptional = <O extends Record<string, unknown>, K extends keyof O>(
  base: O,
  ...keys: K[]
): MakeOptional<O, K> => {
  const newObj: Partial<O> = {};
  for (const key in base) {
    if (!keys.includes(key as unknown as K)) {
      newObj[key] = base[key];
    }
  }

  return newObj as MakeOptional<O, K>;
};

export const CHANNEL_STYLES: Record<
  Channel,
  {
    label: string;
    color: string;
    bg: string;
    borderColor: string;
    handle: string;
  }
> = {
  email: {
    label: "Email",
    color: "#4f46e5",
    bg: "#eef2ff",
    borderColor: "#c7d2fe",
    handle: "support@techhelp.io",
  },
  sms: {
    label: "SMS",
    color: "#059669",
    bg: "#ecfdf5",
    borderColor: "#6ee7b7",
    handle: "+1 (555) 234-5678",
  },
};

// ─── Enriched types ───────────────────────────────────────────────────────────

export type EnrichedThread = Thread & {
  nameWeight: string;
  nameColor: string;
  selectedBg: string;
  selectedBorderColor: string;
  channelLabel: string;
  channelColor: string;
  channelBg: string;
  channelBorderColor: string;
  channelHandle: string;
};

export type EnrichedMessage = {
  alignItems: string;
  bubbleBg: string;
  bubbleColor: string;
  bubbleRadius: string;
  bubbleShadow: string;
  bubbleBorder: string;
};

// ─── Enrichment functions ─────────────────────────────────────────────────────

export function enrichThread(thread: Thread, selectedId: number): EnrichedThread {
  const ch = CHANNEL_STYLES[thread.channel];
  return {
    ...thread,
    nameWeight: thread.unread ? "600" : "400",
    nameColor: thread.unread ? "#111827" : "#374151",
    selectedBg: thread.id === selectedId ? "#fff" : "transparent",
    selectedBorderColor: thread.id === selectedId ? "#4f46e5" : "transparent",
    channelLabel: ch.label,
    channelColor: ch.color,
    channelBg: ch.bg,
    channelBorderColor: ch.borderColor,
    channelHandle: ch.handle,
  };
}

export function enrichMessage(outgoing: boolean): EnrichedMessage {
  const isAgent = outgoing;
  return {
    alignItems: isAgent ? "flex-end" : "flex-start",
    bubbleBg: isAgent ? "#ede9fe" : "#fff",
    bubbleColor: isAgent ? "#fff" : "#111827",
    bubbleRadius: isAgent ? "16px 16px 4px 16px" : "16px 16px 16px 4px",
    bubbleShadow: isAgent ? "none" : "0 1px 4px rgba(0,0,0,0.07)",
    bubbleBorder: isAgent ? "none" : "1px solid #f0f0f8",
  };
}

export function buildHref(path: string, params: Record<string, string | number | boolean | undefined | null> = {}) {
  const searchParams: [string, string][] = [];
  for (const [key, val] of Object.entries(params)) {
    if (val === undefined || val === null || val === "") continue;
    searchParams.push([key, String(val)]);
  }

  const searchQuery = searchParams.length ? "?" + searchParams.map((sp) => `${sp[0]}=${sp[1]}`).join("&") : "";
  return path + searchQuery;
}

export function getFilterChipStyle(filter: ActiveFilter, activeFilter: ActiveFilter) {
  const isActive = filter === activeFilter;
  const isSms = filter === "sms";
  return {
    background: isActive ? (isSms ? "#ecfdf5" : "#eef2ff") : "#fff",
    borderColor: isActive ? (isSms ? "#059669" : "#4f46e5") : "#e5e7eb",
    color: isActive ? (isSms ? "#059669" : "#4f46e5") : "#6b7280",
    fontWeight: isActive ? "600" : "400",
  };
}

export function removeQuotedEmailHistory(text: string): string {
  const replyHeader = /\bOn\s+(?:Mon|Tue|Wed|Thu|Fri|Sat|Sun),?\s+.+?\s+wrote:\s*/is;

  const originalMessage = /-{2,}\s*Original Message\s*-{2,}/is;

  const forwardedMessage = /-{2,}\s*Forwarded message\s*-{2,}/is;

  const match = text.match(replyHeader) ?? text.match(originalMessage) ?? text.match(forwardedMessage);

  if (match?.index !== undefined) {
    return text.slice(0, match.index).trim();
  }

  return text.replace(/^\s*>\s?/gm, "").trim();
}

export function getRiskPlusEmail(appKey?: string): string | undefined {
  if (!appKey) {
    return undefined;
  }

  return `${appKey}@datasense.mindwebs.co`;
}
